import {
  createAsyncThunk,
  createSlice,
  createSelector,
  createAction,
} from '@reduxjs/toolkit';
import {
  Program,
  Status,
  SortingType,
  ProgramLinkedProject,
  filter,
} from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { RootState } from 'state/store';
import { orderProjectsBy, filterProgramProjects } from './helpers';
import { isEmpty, get } from 'lodash';
import programAPI from './programAPI';
import { ProgramProjectFilters } from 'utils/types/filters';

interface programState {
  value: Program;
  status: Status;
  errorCode: number;
  projectsForProgram: ProgramLinkedProject[];
  fetchProjectsForProgramStatus: Status;
  projectsSearchValue: string;
  sorting: {
    orderBy: string;
    order: SortingType;
  };
  filters: ProgramProjectFilters;
  isFiltersOn: boolean;
}

/* ============================= INITIAL STATE ============================== */

const defaultValues = {
  title: '',
  id: '',
  description: '',
  status: '',
  fetchProjectsForProgramStatus: '',
  programOwners: [],
  createdAt: '',
  delivery_type: null,
  program_number: null,
  programProjects: [],
  program_creator_id: '',
  updatedProjects: undefined,
} as Program;

const initialState: programState = {
  value: defaultValues,
  status: SLICE_STATUS.IDLE,
  errorCode: 0,
  fetchProjectsForProgramStatus: SLICE_STATUS.IDLE,
  projectsForProgram: [],
  projectsSearchValue: '',
  sorting: {
    order: 'asc',
    orderBy: '',
  },
  filters: {},
  isFiltersOn: false,
};

/* ============================= ACTIONS ============================== */
export const resetProgram = createAction('program/RESET_PROGRAM');

export const setLinkedProjects = createAction<{
  projects: ProgramLinkedProject[];
}>('program/SET_LINKED_PROJECTS');

export const unlinkProjectFromProgram = createAction<{
  projectId: string;
}>('program/UNLINK_PROJECT');

export const setProgramProjectsTableSearch = createAction(
  'program/SET_PROJECTS_SEARCH',
  (search: string) => {
    return { payload: search };
  }
);

export const setProgramProjectsSortingOptions = createAction(
  'program/SET_ORDERS',
  (column: string, order: SortingType) => {
    return { payload: { order: order, orderBy: column } };
  }
);

export const setProjectsFilters = createAction<filter[]>(
  'program/SET_projects_FILTERS'
);
export const setFilters = createAction<ProgramProjectFilters>(
  'programs/SET_PROJECTS_FILTERS'
);

export const setProjectsFiltersStatus = createAction<boolean>(
  'program/SET_projects_FILTERS_STATUS'
);

export const updateLinkedProject = createAction<{
  project: ProgramLinkedProject;
}>('program/UPDATE_LINKED_PROJECT');

export const updateProjectInProgramList = createAction<{
  projectId: string;
  updatedProject: Partial<ProgramLinkedProject>;
}>('program/UPDATE_PROJECT_IN_PROGRAM_LIST');

/* ============================== REDUX THUNK =============================== */
export const fetchProgram = createAsyncThunk(
  'program/FETCH_PROGRAM',
  async (programId: string, { rejectWithValue }) => {
    try {
      const { data } = await programAPI.fetchProgram(programId);
      return data;
    } catch (error: any) {
      // The axios interceptor rejects 403/404 with a bare status number but
      // throws the AxiosError for other statuses (e.g. 500), so normalize both
      // into the numeric status code consumed by the LoadingError component.
      // Network errors (no response) have no status, so fall back to 0 to keep
      // errorCode a number.
      const code =
        typeof error === 'number' ? error : error?.response?.status ?? 0;
      return rejectWithValue(code);
    }
  }
);

export const fetchProjectsForProgram = createAsyncThunk(
  'programs/FETCH_PROJECTS_FOR_PROGRAM',
  async (programId: string) => {
    try {
      const { data } = await programAPI.fetchProjectsForProgram(programId);
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const updateProgram = createAsyncThunk(
  'programs/UPDATE_PROGRAM',
  async ({
    programId,
    updateFields,
  }: {
    programId: string;
    updateFields: Partial<Program>;
  }) => {
    const response = await programAPI.updateProgram(programId, updateFields);
    if (response.success) {
      return response.data;
    }
    return response;
  }
);

export const updateProgramOwners = createAsyncThunk(
  'programs/SET_OWNERS',
  async ({
    programId,
    ownersIds,
  }: {
    programId: string;
    ownersIds: string[];
  }) => {
    const response = await programAPI.updateProgramOwners(programId, ownersIds);
    if (response.success) {
      return response.data;
    }
    return response;
  }
);

export const updateProgramProjects = createAsyncThunk(
  'programs/SET_PROJECTS',
  async ({
    programId,
    projectIds,
  }: {
    programId: string;
    projectIds: string[];
  }) => {
    const response = await programAPI.updateProgramProjects(
      programId,
      projectIds
    );
    if (response.success) {
      return response.data;
    }
    return response;
  }
);

/* ================================= REDUCER ================================ */
const programSlice = createSlice({
  name: 'program',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProgram.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
        state.errorCode = 0;
      })
      .addCase(fetchProgram.rejected, (state, action) => {
        state.status = SLICE_STATUS.FAILED;
        state.errorCode = action.payload as number;
      })
      .addCase(fetchProgram.fulfilled, (state, action) => {
        state.value = action.payload;
        state.status = SLICE_STATUS.IDLE;
        state.errorCode = 0;
      })
      .addCase(fetchProjectsForProgram.fulfilled, (state, action) => {
        state.fetchProjectsForProgramStatus = SLICE_STATUS.IDLE;
        state.projectsForProgram = action.payload;
      })
      .addCase(fetchProjectsForProgram.pending, (state, action) => {
        state.fetchProjectsForProgramStatus = SLICE_STATUS.LOADING;
      })
      .addCase(fetchProjectsForProgram.rejected, (state, action) => {
        state.fetchProjectsForProgramStatus = SLICE_STATUS.FAILED;
      })
      .addCase(setLinkedProjects, (state, action) => {
        if (!state.value.updatedProjects) {
          state.value.updatedProjects = [
            ...state.value.programProjects,
            ...action.payload.projects,
          ];
        } else {
          state.value.updatedProjects = [
            ...state.value.updatedProjects,
            ...action.payload.projects,
          ];
        }
      })
      .addCase(updateLinkedProject, (state, action) => {
        if (!state.value.updatedProjects) {
          state.value.updatedProjects = [...state.value.programProjects];
        }
        const oldProject = state.value.updatedProjects?.filter(
          (project) => project.id === action.payload.project.id
        );
        state.value.updatedProjects = state.value.updatedProjects?.filter(
          (project) => project.id !== action.payload.project.id
        );
        state.value.updatedProjects.unshift({
          ...oldProject[0],
          ...action.payload.project,
        });
      })
      .addCase(unlinkProjectFromProgram, (state, action) => {
        if (!state.value.updatedProjects) {
          state.value.updatedProjects = state.value.programProjects.filter(
            (project) => project.id !== action.payload.projectId
          );
        } else {
          state.value.updatedProjects = state.value.updatedProjects.filter(
            (project) => project.id !== action.payload.projectId
          );
        }
      })
      .addCase(resetProgram, (state) => {
        state.value = defaultValues;
        state.status = SLICE_STATUS.IDLE;
        state.errorCode = 0;
        state.projectsSearchValue = '';
        state.sorting = {
          order: 'asc',
          orderBy: '',
        };
      })
      .addCase(setProgramProjectsSortingOptions, (state, action) => {
        state.sorting = action.payload;
      })
      .addCase(setProgramProjectsTableSearch, (state, action) => {
        state.projectsSearchValue = action.payload;
      })
      .addCase(setFilters, (state, action) => {
        state.filters = action.payload;
      })
      .addCase(setProjectsFiltersStatus, (state, action) => {
        state.isFiltersOn = action.payload;
      })
      .addCase(updateProgram.fulfilled, (state, action) => {
        if (get(action, 'payload.updatedProgram', null)) {
          state.value = { ...state.value, ...action.payload.updatedProgram };
        }
      })
      .addCase(updateProgramOwners.fulfilled, (state, action) => {
        if (get(action, 'payload.programOwners', null)) {
          state.value.programOwners = action.payload.programOwners;
        }
      })
      .addCase(updateProgramProjects.fulfilled, (state, action) => {
        if (get(action, 'payload.programProjects', null)) {
          state.value.programProjects = action.payload.programProjects;
          state.value.updatedProjects = undefined;
        }
      })
      .addCase(updateProjectInProgramList, (state, action) => {
        const { projectId, updatedProject } = action.payload;

        if (state.value.updatedProjects) {
          const projectIndex = state.value.updatedProjects.findIndex(
            (project) => project.id === projectId
          );
          if (projectIndex !== -1) {
            state.value.updatedProjects[projectIndex] = {
              ...state.value.updatedProjects[projectIndex],
              ...updatedProject,
            };
          }
        }

        const programProjectIndex = state.value.programProjects.findIndex(
          (project) => project.id === projectId
        );
        if (programProjectIndex !== -1) {
          state.value.programProjects[programProjectIndex] = {
            ...state.value.programProjects[programProjectIndex],
            ...updatedProject,
          };
        }
      });
  },
});

/* =============================== SELECTORS ================================ */

export const selectProgramSliceStatus = (state: RootState) =>
  state.program.status;
export const selectProjectsForProgramSliceStatus = (state: RootState) =>
  state.program.fetchProjectsForProgramStatus;
export const getProgramData = (state: RootState) => {
  const updatedProjects = get(state, 'program.value.updatedProjects');
  if (!updatedProjects) {
    return {
      ...state.program.value,
      updatedProjects: state.program.value.programProjects,
    };
  }
  return state.program.value;
};

export const getProgramId = (state: RootState) => state.program.value.id;
export const getProgramTitle = (state: RootState) => state.program.value.title;

export const getProgramDescription = (state: RootState) =>
  state.program.value.description;

export const selectProgramProjectsSorting = (state: RootState) =>
  state.program.sorting;
export const selectProgramProjectsTableSearch = (state: RootState) =>
  state.program.projectsSearchValue;
export const selectProgramProjectsTableFilters = (state: RootState) =>
  state.program.filters;
export const selectProgramProjectsTableFiltersStatus = (state: RootState) =>
  state.program.isFiltersOn;

export const selectProgramStatus = (state: RootState) => state.program.status;

export const selectProgramErrorCode = (state: RootState) =>
  state.program.errorCode;

export const selectProgramWithFilteredProjects = createSelector(
  [
    getProgramData,
    selectProgramProjectsTableSearch,
    selectProgramProjectsSorting,
    selectProgramProjectsTableFilters,
  ],
  (program, searchValue, sorting, filters) => {
    let filteredProgram = { ...program };
    let linkedProjectsList = program.updatedProjects || [];
    if (!isEmpty(linkedProjectsList)) {
      const filteredProjects = filterProgramProjects(
        linkedProjectsList,
        searchValue,
        filters
      );
      const sortedProjects = orderProjectsBy(
        filteredProjects,
        sorting.orderBy,
        sorting.order
      );
      filteredProgram.updatedProjects = sortedProjects;
    }

    return filteredProgram;
  }
);

export const getAllProjectsForProgram = (state: RootState) =>
  state.program.projectsForProgram;

export const getAvailabeProjectsForLinking = createSelector(
  [getProgramData, getAllProjectsForProgram],
  (program: Program, projects: ProgramLinkedProject[]) =>
    projects.filter(
      (project) =>
        !program.updatedProjects!.some(
          (programProject) => programProject.id === project.id
        )
    )
);

export default programSlice.reducer;
